import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

/**
 * Send-DocuSign-link SMS panel shown after a DocuSign envelope is created.
 * Mirrors the GoCardless mandate SMS panel UX (editable body, status badge).
 *
 * Props:
 *   envelope: { envelope_id }
 *   signers: [{name, email}]                — used to pick the recipient
 *   defaultMobile: string                   — pre-fill from director phone if available
 *   aircallNumbers: [{id, name, digits}]    — for "Sending from" display
 *   aircallPhoneId: string                  — selected aircall phone_id
 *   loadAircallNumbers: () => void          — to lazily load if not yet loaded
 *   loadingNumbers: boolean
 *   defaultName: string
 *   businessName: string
 *   sfaNumber: string
 */
export default function DocuSignSmsPanel({
  envelope,
  signers = [],
  defaultMobile = "",
  aircallNumbers,
  aircallPhoneId,
  loadAircallNumbers,
  loadingNumbers,
  defaultName,
  businessName,
  sfaNumber,
}) {
  const [smsTo, setSmsTo] = useState(defaultMobile);
  const [smsBody, setSmsBody] = useState("");
  const [recipientEmail, setRecipientEmail] = useState(signers[0]?.email || "");
  const [recipientName, setRecipientName] = useState(signers[0]?.name || defaultName || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Load template + pre-substitute placeholders
  useEffect(() => {
    if (!envelope?.envelope_id) return;
    (async () => {
      try {
        const r = await api.get("/aus/docusign-sms-template");
        const tmpl = r.data?.template || "";
        const firstName = (defaultName || "").trim().split(/\s+/)[0] || "there";
        const rendered = tmpl
          .replace(/\{firstName\}/g, firstName)
          .replace(/\{customerName\}/g, defaultName || "")
          .replace(/\{businessName\}/g, businessName || "")
          .replace(/\{scheme\}/g, "")
          .replace(/\{sfaNumber\}/g, sfaNumber || "");
        setSmsBody(rendered);
      } catch (e) {
        console.error("Failed to load DocuSign SMS template", e);
      }
    })();
    loadAircallNumbers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelope?.envelope_id]);

  // Keep recipient in sync when signers change
  useEffect(() => {
    if (signers.length > 0 && !recipientEmail) {
      setRecipientEmail(signers[0].email || "");
      setRecipientName(signers[0].name || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signers]);

  const sendSms = async () => {
    if (!smsTo) { toast.error("Enter the customer's mobile number"); return; }
    if (!recipientEmail) { toast.error("Pick a signer recipient"); return; }
    if (!aircallPhoneId) { toast.error("No portal SMS line configured"); return; }
    setSending(true);
    setResult(null);
    try {
      const resp = await api.post("/aus/docusign/send-signing-sms", {
        customer_mobile: smsTo,
        customer_name: recipientName || defaultName,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        envelope_id: envelope.envelope_id,
        business_name: businessName,
        sfa_number: sfaNumber,
        aircall_phone_id: aircallPhoneId,
        custom_body: smsBody || undefined,
      });
      setResult({ success: true, ...resp.data });
      toast.success(`SMS sent to ${resp.data.to}`);
    } catch (err) {
      const msg = errorDetailMessage(err, err.message || "Failed to send SMS");
      setResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!envelope?.envelope_id) return null;

  const selectedAircall = aircallNumbers?.find((n) => String(n.id) === String(aircallPhoneId));

  return (
    <div
      className="mt-4 p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-3"
      data-testid="docusign-sms-panel"
    >
      <div className="flex items-center justify-between">
        <Label className="text-violet-800 font-medium flex items-center gap-2 text-sm">
          <Send className="w-4 h-4" />
          Send signing link via SMS
        </Label>
      </div>
      <p className="text-xs text-violet-700">
        Customer receives a shortened link. Each click generates a fresh DocuSign URL — link never expires.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-violet-800">Mobile (AU)</Label>
          <Input
            placeholder="04xx xxx xxx"
            value={smsTo}
            onChange={(e) => setSmsTo(e.target.value)}
            className="bg-white h-9"
            data-testid="docusign-sms-to-input"
          />
        </div>
        <div>
          <Label className="text-xs text-violet-800">Signer recipient</Label>
          <select
            value={recipientEmail}
            onChange={(e) => {
              setRecipientEmail(e.target.value);
              const s = signers.find((x) => x.email === e.target.value);
              setRecipientName(s?.name || "");
            }}
            className="w-full h-9 rounded-md border bg-white px-3 text-sm"
            data-testid="docusign-sms-signer-select"
          >
            {signers.length === 0 && <option value="">No signers</option>}
            {signers.map((s, i) => (
              <option key={`${s.email}-${i}`} value={s.email}>
                {s.name || s.email}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs text-violet-800">Sending from</Label>
          <div className="h-9 rounded-md border border-violet-200 bg-white px-3 flex items-center text-sm text-slate-700">
            {loadingNumbers && <Loader2 className="w-3 h-3 mr-2 animate-spin text-slate-400" />}
            {!loadingNumbers && (
              selectedAircall ? (
                <span className="truncate">
                  <span className="font-medium">{selectedAircall.digits}</span>
                </span>
              ) : (
                <span className="text-slate-500">No portal SMS line</span>
              )
            )}
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-violet-800">SMS message</Label>
          <span className="text-[10px] text-muted-foreground">
            {smsBody.length} chars{" "}
            {smsBody.length > 160 && (
              <span className="text-amber-700">({Math.ceil(smsBody.length / 160)} segments)</span>
            )}
          </span>
        </div>
        <Textarea
          value={smsBody}
          onChange={(e) => setSmsBody(e.target.value)}
          rows={3}
          className="bg-white text-sm font-mono resize-y"
          data-testid="docusign-sms-body-textarea"
          placeholder="Loading template…"
        />
        <p className="text-[11px] text-muted-foreground">
          <code className="bg-white px-1 py-0.5 rounded">{"{shortUrl}"}</code> is replaced with the actual short link on send.
        </p>
      </div>

      <Button
        size="sm"
        onClick={sendSms}
        disabled={sending || !smsTo || !aircallPhoneId || !smsBody || !recipientEmail}
        className="bg-violet-600 hover:bg-violet-700"
        data-testid="docusign-send-sms-btn"
      >
        {sending ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending SMS…</>
        ) : (
          <><Send className="w-4 h-4 mr-2" />Send signing SMS</>
        )}
      </Button>

      {result?.success && (
        <div className="text-xs bg-white border border-violet-200 rounded-md p-2 space-y-1" data-testid="docusign-sms-success">
          <p className="text-violet-700">✅ Sent to {result.to}</p>
          <p className="text-muted-foreground break-all">Short link: {result.short_url}</p>
          <p className="text-muted-foreground">Preview: {result.sms_preview} ({result.length} chars)</p>
        </div>
      )}
      {result && !result.success && (
        <p className="text-xs text-red-600" data-testid="docusign-sms-error">{result.error}</p>
      )}
    </div>
  );
}
